import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { EASE } from "@/components/motion/Reveal";
import { Rich } from "@/components/common/Rich";

// Inner-page hero: masked headline + clipped, parallax image.
export const PageHero = ({ hero, testId, children }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section ref={ref} className="relative overflow-hidden pb-16 pt-28 sm:pt-36 lg:pb-24" data-testid={testId}>
      <div className="container-x">
        <motion.p className="eyebrow mb-12 flex items-center gap-3 border-y border-line py-3 lg:mb-16" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <span className="cross" />
          {hero.eyebrow}
        </motion.p>
      </div>
      <div className="container-x grid items-end gap-12 lg:grid-cols-12">
        <div className={hero.image ? "lg:col-span-7" : "lg:col-span-10"}>
          <MaskedLines lines={hero.lines} className="h-display text-ink" testId={`${testId}-headline`} />
          <motion.p
            className="lead mt-8 max-w-2xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
          >
            <Rich text={hero.body} />
          </motion.p>
          {children}
        </div>
        {hero.image && (
          <motion.div
            className="relative h-[340px] overflow-hidden border border-line bg-[#0E1726] sm:h-[460px] lg:col-span-5 lg:h-[520px]"
            initial={reduce ? false : { clipPath: "inset(0% 0% 100% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.25 }}
          >
            <motion.img src={hero.image} alt="" className="absolute inset-0 h-[120%] w-full object-cover" style={{ y }} />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1220]/40 to-transparent" />
            <span className="absolute left-0 top-0 bg-[#0B1220]/80 px-2.5 py-1 font-mono text-[12px] uppercase tracking-[0.12em] text-white">Fig. 01</span>
          </motion.div>
        )}
      </div>
    </section>
  );
};
